import { useMounted } from '@hooks/useMounted';
import { uuid } from '@lib/random';
import { Map } from 'immutable';
import { camelCase, upperFirst } from 'lodash';
import * as React from 'react';
import { app } from '../../app';
import { call } from '../../functions';
import { NotificationServiceProvider } from '../../notifications';
import { RouterServiceProvider } from '../../router/lib/RouterServiceProvider';

export interface ActionType<P = any> {
    type: string;
    payload: P;
    rest: any[];
}

interface ContextOptions<S> {
    initialState: any;
    debug?: boolean;
    stateExtender?: (initialState: any, newState: any) => any;
    actions?: {
        [name: string]: (payload: any, rest: any[], actions: any, ctx: React.Context<S>, key: string) => any;
    };
    events?: {
        [name: string]: <T>(state: S, action: ActionType<T>, actions: any, ctx: React.Context<S>, key: string) => S;
    };
}

/**
 * Create a custom Context Fast & clean
 * @tutorial https://youtu.be/zV4stLSQXzs
 * @param name
 * @param options
 */
export function deprecated_createContext(name: string, options: ContextOptions<any>) {
    type ContextType = [
        typeof options.initialState,
        typeof options.actions,
        {
            dispatch: (type: string, payload: any, ...rest: any[]) => void;
            getState: () => typeof options.initialState;
        },
    ];

    // @ts-ignore
    const ctx = React.createContext<ContextType>([] as any);

    if (!options.initialState) {
        options.initialState = Map({});
    }

    if (!options.initialState.events) {
        // @ts-ignore
        options.initialState.events = {};
    }

    if (!options.initialState.actions) {
        // @ts-ignore
        options.initialState.actions = {};
    }

    // @ts-ignore
    ctx.opts$ = options;
    // @ts-ignore
    ctx.uuid$ = `${name}@${uuid()}`;

    // @ts-ignore
    ctx.actions$ = Object.keys(options.actions).reduce((actions, key) => {
        if (key !== camelCase(key)) {
            throw wrongCamelCaseName('Actions', key);
        }

        return {
            ...actions,
            [key]: (payload$, ...rest) => {
                if (!rest) {
                    rest = [];
                }

                // @ts-ignore
                const payload = options.actions[key](payload$, rest, ctx.dispatchActions$, ctx, key);
                return {
                    type: key,
                    payload,
                    rest,
                };
            },
        };
    }, {});

    // @ts-ignore
    ctx.events$ = Object.keys(options.events).reduce((events, key) => {
        if (key !== camelCase(key)) {
            throw wrongCamelCaseName('Events', key);
        }

        if (!/^on/i.test(key)) {
            throw wrongEventName('Event', key);
        }

        const actionType = camelCase(key.slice(2));

        // @ts-ignore
        if (!ctx.actions$.hasOwnProperty(actionType) || typeof ctx.actions$[actionType] !== 'function') {
            throw undefinedAction(key, actionType);
        }

        return {
            ...events,
            [key]: (state, action) => {
                // @ts-ignore
                return options.events[key](state, action, ctx.dispatchActions$, ctx, key);
            },
        };
    }, {});

    const Provider = React.memo<any>(function Provider$({ onChange, state, children, actionsRef }: any) {
        const isMounted = useMounted();

        const $services = {
            notify: app(NotificationServiceProvider),
            router: app(RouterServiceProvider),
        };

        // @ts-ignore
        ctx.$getServices = (prop) => {
            if (prop) {
                return $services[prop];
            }
            return $services;
        };

        // @ts-ignore
        ctx.$service = (prop, func) => {
            if (func && isMounted()) {
                // @ts-ignore
                return call(func, ctx.$getServices(prop));
            }
        };

        const initialState$ =
            typeof options.stateExtender === 'function'
                ? options.stateExtender(options.initialState, state)
                : state || options.initialState;
        const reducer = React.useCallback(
            (state: typeof options.initialState, action: ActionType) => {
                let newState = state;
                const eventName = `on${upperFirst(camelCase(action.type))}`;

                // @ts-ignore
                if (ctx.opts$.debug) {
                    console.groupCollapsed(`Event ${name}.${eventName}`);
                }

                // @ts-ignore
                let func = ctx.events$.onDefault;

                // @ts-ignore
                if (typeof ctx.events$[eventName] === 'function') {
                    // @ts-ignore
                    func = ctx.events$[eventName];
                }

                if (typeof func === 'function') {
                    newState = func(state, action);
                }

                if (typeof onChange === 'function') {
                    // @ts-ignore
                    onChange(newState, action, ctx.dispatchActions$, ctx);
                }

                return newState;
            },
            [
                // @ts-ignore
                ctx.uuid$,
            ],
        );

        // @ts-ignore
        const [state$, dispatch] = React.useReducer(reducer, initialState$);

        // @ts-ignore
        ctx.dispatchActions$ = Object.keys(ctx.actions$).reduce(
            (actions, key) => ({
                ...actions,
                [key]: (...args) => {
                    setTimeout(() => {
                        // Fire the action immediate to avoid unmounted action
                        if (isMounted()) {
                            // @ts-ignore
                            dispatch(ctx.actions$[key](...args));
                        }
                    });
                },
            }),
            {},
        );

        // @ts-ignore
        ctx.dispatchActions$.dispatch = (type, payload, ...rest) => {
            if (!rest) {
                rest = [];
            }

            if (isMounted()) {
                dispatch({
                    type,
                    payload,
                    rest,
                });
            }
        };

        if (actionsRef) {
            // @ts-ignore
            actionsRef.current = ctx.dispatchActions$;
        }

        return (
            <ctx.Provider
                value={[
                    state$,
                    // @ts-ignore
                    ctx.dispatchActions$,
                    {
                        // @ts-ignore
                        dispatch: ctx.dispatchActions$.dispatch,
                        getState: () => state$,
                    },
                ]}
            >
                {children}
            </ctx.Provider>
        );
    });

    Provider.displayName = `createContext(${name}.Provider)`;
    ctx.displayName = `${name}`;

    return {
        Context: ctx,
        Provider,
    };
}

/**
 * Simple action used to pass payload without modify it
 * @param payload
 * @returns {*}
 */
export const passThrough = (payload: any): any => payload;

const wrongCamelCaseName = (name: string, key: string) =>
    new Error(`${name} should be camelCase naming!
    Expected: '${camelCase(key)}'
    Received: '${key}';
`);

const wrongEventName = (name: string, key: string) =>
    new Error(`${name} '${key}' should be onCamelCase naming!
    Expected: 'on${upperFirst(camelCase(key))}'
    Received: '${key}';
`);

const undefinedAction = (name: string, key: string) =>
    new Error(`There's no action defined for event '${name}'!
    Expected: Action '${key}' to be defined;
    Received: Undefined action '${key}';
`);
